export const STORAGE_VERSION = 1;

export function emptyWorkspace() {
  return { version: STORAGE_VERSION, packs: [], progress: {}, packId: "apc-2025-clairmont-v1", phase: "pre", taskId: "A" };
}

// Also used for AI drafts: reject malformed content before it becomes interactive.
export function validatePack(pack) {
  const text = (value, max = 12000) => typeof value === "string" && value.trim().length > 0 && value.length <= max;
  const list = (value, max = 30) => Array.isArray(value) && value.length <= max && value.every((item) => text(item));
  if (!pack || !/^[a-zA-Z0-9_-]{1,100}$/.test(pack.id) || !text(pack.title, 200) || !/^20\d{2}$/.test(String(pack.year)) || !text(pack.overview) || !Array.isArray(pack.sources) || pack.sources.length > 8 || !pack.sources.every((source) => source && text(source.id, 100) && text(source.title, 200) && text(source.detail, 1000))) {
    throw new Error("The case pack needs a valid ID, year, title, overview and source list.");
  }
  if (pack.status !== undefined && !text(pack.status, 300)) throw new Error("The case-pack status must be plain text.");
  if (!Array.isArray(pack.tasks) || pack.tasks.length < 1 || pack.tasks.length > 15) throw new Error("A case pack must contain 1–15 tasks.");
  const ids = new Set();
  pack.tasks.forEach((task) => {
    if (!task || !/^[A-Z0-9_-]{1,24}$/.test(task.id) || ids.has(task.id) || !text(task.title, 200) || !text(task.area, 100) || !text(task.recipient, 200) || !text(task.requirement) || !text(task.preRelease) || !text(task.pitfall) || !Number.isInteger(task.minutes) || task.minutes < 1 || task.minutes > 180 || !["facts", "references", "checkpoints", "outline"].every((key) => list(task[key]) && task[key].length > 0) || !Array.isArray(task.questions) || task.questions.length > 15) {
      throw new Error("Each task needs a unique ID, requirement, preparation note, facts, references, checkpoints, outline and practice time.");
    }
    ids.add(task.id);
    const questionIds = new Set();
    task.questions.forEach((question) => {
      if (!question || !text(question.id, 100) || questionIds.has(question.id) || !text(question.question) || !list(question.options, 6) || question.options.length < 2 || !Number.isInteger(question.answer) || question.answer < 0 || question.answer >= question.options.length || !text(question.explanation)) throw new Error("A quiz question has invalid choices, answer or explanation.");
      questionIds.add(question.id);
    });
  });
  return pack;
}

export function readWorkspace(key, storage = window.localStorage) {
  const raw = storage.getItem(key);
  if (!raw) return emptyWorkspace();
  const value = JSON.parse(raw);
  if (!value || value.version !== STORAGE_VERSION || !Array.isArray(value.packs) || value.packs.length > 20 || !value.progress || typeof value.progress !== "object" || Array.isArray(value.progress)) throw new Error("Saved study data is not compatible. Export or recover your browser data before resetting it.");
  value.packs.forEach(validatePack);
  if (new Set(value.packs.map((pack) => pack.id)).size !== value.packs.length || value.packs.some((pack) => pack.id === "apc-2025-clairmont-v1")) throw new Error("Saved case-pack IDs are duplicated.");
  const record = (entry) => entry && typeof entry === "object" && !Array.isArray(entry);
  for (const tasks of Object.values(value.progress)) {
    if (!record(tasks)) throw new Error("Saved task progress is damaged.");
    for (const work of Object.values(tasks)) {
      if (!record(work)) throw new Error("Saved task progress is damaged.");
      for (const [field, max] of Object.entries({ answer: 24000, notes: 20000, plan: 12000, reviewedAnswer: 24000 })) {
        if (work[field] !== undefined && work[field] !== null && (typeof work[field] !== "string" || work[field].length > max)) throw new Error("Saved writing is damaged.");
      }
      if (work.checks && (!record(work.checks) || !Object.values(work.checks).every((checked) => typeof checked === "boolean"))) throw new Error("Saved self-review is damaged.");
      if (work.quiz && (!record(work.quiz) || !Object.values(work.quiz).every((answer) => Number.isInteger(answer) && answer >= 0 && answer < 6))) throw new Error("Saved quiz data is damaged.");
      if (work.timer && (!record(work.timer) || Object.values(work.timer).some((number) => !Number.isFinite(number) || number < 0))) throw new Error("Saved timer data is damaged.");
    }
  }
  return { ...emptyWorkspace(), ...value };
}

export function quizResult(task, work) {
  const answers = work?.quiz || {};
  return {
    answered: task.questions.filter((q) => Number.isInteger(answers[q.id])).length,
    correct: task.questions.filter((q) => answers[q.id] === q.answer).length,
    total: task.questions.length,
  };
}

export function taskNeedsRevision(task, work = {}) {
  return Boolean(work.answer?.trim()) && (work.reviewedAnswer !== work.answer || task.checkpoints.some((_, i) => !work.checks?.[i]));
}

export function downloadFile(name, content, type = "application/json") {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
